import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Forward, Info, Loader2, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/misc'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { i18n } from '@/lib/i18n'
import {
  useConversations,
  useForwardMessage,
  type ConversationListItem,
} from '@/hooks/use-conversations'
import { platformLabel } from './lib'

/** Backend chỉ nhận tối đa 10 hội thoại đích cho mỗi lần chuyển tiếp. */
const MAX_TARGETS = 10

interface Props {
  sourceConvId: string
  /** null = không có tin nào được chọn → hộp thoại coi như đóng */
  messageId: string | null
  /** Nội dung văn bản của tin; rỗng ⇒ tin không phải văn bản, không chuyển tiếp được */
  messageText: string
  /** contentType của tin (vd 'text', 'file', 'image'…) — chỉ 'text' mới chuyển tiếp được, xem canForward */
  messageContentType: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Một dòng lỗi sau khi gửi, đã ghép sẵn tên hội thoại để hiển thị. */
interface FailedRow {
  conversationId: string
  name: string
  error: string
}

function convName(conv: ConversationListItem): string {
  return conv.displayName || conv.contact?.fullName || i18n.t('convChat:forward.unnamed')
}

/**
 * Hộp thoại "Chuyển tiếp tin nhắn": chọn tối đa 10 hội thoại đích rồi gửi.
 *
 * Ràng buộc phía backend được phản ánh ngay trên giao diện:
 *  - Tối đa 10 hội thoại mỗi lần.
 *  - Chỉ chuyển tiếp được tin văn bản (ảnh/sticker/tệp sẽ bị từ chối).
 *  - Hội thoại đích phải cùng tài khoản kênh với hội thoại nguồn, nếu khác
 *    sẽ nằm trong danh sách `failed` của kết quả trả về.
 */
export function ForwardDialog({
  sourceConvId,
  messageId,
  messageText,
  messageContentType,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation('convChat')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [failedRows, setFailedRows] = useState<FailedRow[]>([])

  const forward = useForwardMessage(sourceConvId)

  // Chỉ tin contentType 'text' mới chuyển tiếp được — tin ảnh/file/video/sticker vẫn có
  // `content` không rỗng (chú thích, hoặc với tin file là JSON nội bộ Zalo dùng để mô tả file
  // share), nhưng KHÔNG phải văn bản gửi được nguyên trạng. Khớp đúng điều kiện backend
  // (`chat-actions-routes.ts`#forward) để không hiện nút "Chuyển tiếp" cho tin backend sẽ từ chối.
  const preview = messageText.trim()
  const isTextMessage = messageContentType === 'text' && preview.length > 0

  // Debounce ô tìm kiếm 400ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(timer)
  }, [search])

  // Đóng hộp thoại → xoá toàn bộ trạng thái tạm
  useEffect(() => {
    if (open) return
    setSearch('')
    setDebouncedSearch('')
    setSelected([])
    setFailedRows([])
  }, [open])

  const { data, isLoading, isError, error } = useConversations({
    search: debouncedSearch || undefined,
    limit: 50,
  })

  // Bỏ hội thoại nguồn khỏi danh sách đích
  const targets = useMemo(
    () => (data?.conversations ?? []).filter((conv) => conv.id !== sourceConvId),
    [data, sourceConvId],
  )

  const atLimit = selected.length >= MAX_TARGETS

  function toggle(id: string) {
    setFailedRows([])
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= MAX_TARGETS) {
        toast.warning(t('forward.maxPerTime', { max: MAX_TARGETS }))
        return prev
      }
      return [...prev, id]
    })
  }

  const canSend =
    !!messageId && isTextMessage && selected.length > 0 && !forward.isPending

  async function handleForward() {
    if (!canSend || !messageId) return
    setFailedRows([])
    try {
      const result = await forward.mutateAsync({
        messageId,
        conversationIds: selected,
      })
      const failed = result.failed ?? []

      if (failed.length === 0) {
        toast.success(t('forward.forwardedTo', { count: result.sent }))
        onOpenChange(false)
        return
      }

      // Có lỗi một phần → giữ hộp thoại mở để người dùng xem lý do
      const byId = new Map(targets.map((conv) => [conv.id, convName(conv)]))
      setFailedRows(
        failed.map((f) => ({
          conversationId: f.conversationId,
          name: byId.get(f.conversationId) || f.conversationId,
          error: f.error,
        })),
      )
      setSelected(failed.map((f) => f.conversationId))

      if (result.sent > 0) {
        toast.warning(t('forward.partial', { sent: result.sent, failed: failed.length }))
      } else {
        toast.error(t('forward.allFailed', { count: failed.length }))
      }
    } catch (err) {
      toast.error(apiError(err) || t('forward.failed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('forward.title')}</DialogTitle>
          <DialogDescription>
            {t('forward.description', { max: MAX_TARGETS })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Xem trước nội dung sắp chuyển tiếp */}
          <div className="rounded-lg bg-muted p-3">
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {t('forward.contentLabel')}
            </p>
            <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm">
              {preview || t('forward.noText')}
            </p>
          </div>

          {/* Cảnh báo tin không phải văn bản */}
          {!isTextMessage && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p>
                {t('forward.textOnly')}
              </p>
            </div>
          )}

          {/* Chú thích: đích phải cùng tài khoản kênh với hội thoại nguồn */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              {t('forward.sameChannelNote')}
            </p>
          </div>

          {/* Ô tìm kiếm */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              autoComplete="off"
              placeholder={t('forward.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Danh sách hội thoại đích */}
          <div className="max-h-72 overflow-y-auto rounded-lg border">
            {isLoading ? (
              <Loading className="py-10" label={t('forward.loading')} />
            ) : isError ? (
              <EmptyState
                icon={AlertTriangle}
                title={t('forward.loadError')}
                description={apiError(error) || undefined}
              />
            ) : targets.length === 0 ? (
              <EmptyState
                title={t('forward.noMatch')}
                description={
                  debouncedSearch
                    ? t('forward.tryOtherKeyword')
                    : t('forward.noOtherConv')
                }
              />
            ) : (
              <ul className="divide-y">
                {targets.map((conv) => {
                  const checked = selected.includes(conv.id)
                  const disabled = !checked && atLimit
                  return (
                    <li key={conv.id}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors hover:bg-muted/60',
                          checked && 'bg-primary/5',
                          disabled && 'cursor-not-allowed opacity-50',
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => toggle(conv.id)}
                        />
                        <Avatar className="h-8 w-8">
                          {conv.contact?.avatarUrl && (
                            <AvatarImage src={conv.contact.avatarUrl} alt="" />
                          )}
                          <AvatarFallback>{initials(convName(conv))}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {convName(conv)}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {conv.channelAccount?.displayName ||
                              platformLabel(conv.channelAccount?.platform)}
                          </p>
                        </div>
                      </label>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {/* Kết quả lỗi một phần */}
          {failedRows.length > 0 && (
            <div className="space-y-1.5 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
              <p className="text-sm font-medium">
                {t('forward.notReceived', { count: failedRows.length })}
              </p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {failedRows.map((row) => (
                  <li key={row.conversationId} className="break-words">
                    <span className="font-medium text-foreground">{row.name}</span>
                    {' — '}
                    {row.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {t('forward.selectedCount', { n: selected.length, max: MAX_TARGETS })}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={forward.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button type="button" onClick={handleForward} disabled={!canSend}>
              {forward.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Forward className="h-4 w-4" />
              )}
              {t('forward.action')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
